import { Modal, Tag } from 'antd';
import { BookOpen, Calendar, BarChart3, Check, X } from 'lucide-react';
import type { FC } from 'react';
import type { KnowledgeBrick } from './KnowledgeBrickTable';

interface Props {
  brick: KnowledgeBrick | null;
  open: boolean;
  onClose: () => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  argument_vente: 'blue',
  case_study: 'green',
  technical_spec: 'purple',
  testimonial: 'gold',
  competitive_advantage: 'red',
  faq: 'default',
  objection_response: 'geekblue',
};

const CATEGORY_LABELS: Record<string, string> = {
  argument_vente: 'Argument de vente',
  case_study: 'Case study',
  technical_spec: 'Spécification technique',
  testimonial: 'Témoignage',
  competitive_advantage: 'Avantage concurrentiel',
  faq: 'FAQ',
  objection_response: 'Réponse à objection',
};

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  markdown: { label: 'Markdown', color: 'cyan' },
  feedback: { label: 'Feedback', color: 'orange' },
  manual: { label: 'Manuel', color: 'default' },
};

const formatDate = (iso: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const KnowledgeBrickDetailModal: FC<Props> = ({ brick, open, onClose }) => {
  if (!brick) return null;

  const personas = Array.isArray(brick.personaTarget)
    ? brick.personaTarget
    : brick.personaTarget
      ? [brick.personaTarget]
      : [];

  const source = SOURCE_LABELS[brick.sourceType];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      title={
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-500" />
          <span>Détail de la brique</span>
        </div>
      }
    >
      <div className="space-y-5 pt-2">
        {/* Metadata badges */}
        <div className="flex flex-wrap items-center gap-2">
          <Tag color={CATEGORY_COLORS[brick.category] ?? 'default'}>
            {CATEGORY_LABELS[brick.category] ?? brick.category}
          </Tag>
          <Tag color={source?.color ?? 'default'}>{source?.label ?? brick.sourceType}</Tag>
          {personas.map((p) => (
            <Tag key={p} className="uppercase">{p}</Tag>
          ))}
          <Tag
            color={brick.isVerified ? 'success' : 'warning'}
            icon={brick.isVerified ? <Check className="inline w-3 h-3 mr-1" /> : <X className="inline w-3 h-3 mr-1" />}
          >
            {brick.isVerified ? 'Vérifié' : 'Non vérifié'}
          </Tag>
        </div>

        {/* Content */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-4">
          <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            {brick.content}
          </p>
        </div>

        {/* Footer metadata */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" />
            <span>{brick.usageCount} usage{brick.usageCount !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            <span>Créé le {formatDate(brick.createdAt)}</span>
          </div>
          {brick.updatedAt && brick.updatedAt !== brick.createdAt && (
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              <span>MAJ le {formatDate(brick.updatedAt)}</span>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default KnowledgeBrickDetailModal;
